import React from "react";
import { FeaturePageShell } from "./FeaturePageShell";
import mapHeader from "./assets/map/map_header.jpg";
import mapNY from "./assets/map/map_NY.jpg";
import mapLondon from "./assets/map/map_londres.jpg";
import mapSection1 from "./assets/map/map_section1.mp4";
import mapSection5 from "./assets/map/map_section5.mp4";

const before = [
  ["Outdated imagery", "A rain-soaked storefront photo uploaded four years ago by a stranger still defines your brand today."],
  ["Zero merchant control", "Algorithms favor random tourist snapshots over your daily craft, seasonal menu, or newly renovated space."],
  ["Temporal blindness", "A shop closed for annual leave looks identical to a bakery pulling steaming sourdough fresh from the deck oven."],
  ["The guesswork tax", "Customers travel blind, unsure whether an address is packed, empty, under construction, or hosting a secret tasting."],
];

const now = [
  ["Total editorial sovereignty", "The baker triggers a snapshot at 6:30 AM when croissants hit the cooling rack; the wine merchant updates at 6:30 PM for Friday evening tastings. You choose the exact moment your space deserves to be seen."],
  ["The latest update is truth", 'Pins are dynamic micro-clips showing the single freshest verified pulse of the venue, stamped with clear recency tags ("Updated 14m ago", "This morning 07:15", "Yesterday").'],
  ["Zero surveillance creep", "No 24/7 video streams or privacy-invasive feeds. Just authentic, intentional visual checkpoints that reflect real daily activity."],
  ["One-tap creator delegation", "Too busy behind the counter? Tap to trigger a quick bounty on the map; a certified local Weaver nearby captures and uploads the missing cut against an automated escrow payment."],
];

const newYork = [
  {
    title: '"Living Weave" Chelsea Market',
    category: "Food & Retail",
    onScreen: 'Busy market hall, 4.8★ rating, 22 active Weavers, "Order Ahead" link.',
    value: "22 local contributors continuously hot-swap daily specials, new vendor stalls, and crowd levels, turning the video into a real-time food guide with direct ordering.",
  },
  {
    title: '"Living Weave" Grand Central Terminal',
    category: "Transit & Architecture",
    onScreen: "Close-up of the iconic brass concourse clock, arrivals and departures feed.",
    value: "Captures the continuous pulse of the transit hub across seasons and rush hours through a single persistent URL that compounds watch time.",
  },
  {
    title: '"Living Weave" Eleven Madison Park',
    category: "Fine Dining",
    onScreen: 'Chef plating a fine-dining course, "$$$$" pricing, "Reserve table" CTA.',
    value: "The video updates alongside seasonal menu rotations, transforming traditional promotional footage into an active, bookable culinary portfolio.",
  },
  {
    title: '"Living Weave" McSorley\'s Ale House',
    category: "Heritage & Nightlife",
    onScreen: 'Packed historic pub crowd, live operating status, "$$" price badge.',
    value: "Operates as a live vibe check where patrons and local creators inject fresh evening footage, keeping the venue's search ranking at peak authority.",
  },
  {
    title: '"Living Weave" Hamilton',
    category: "Broadway & Performing Arts",
    onScreen: 'Live stage cast performance, showtime schedule, "Buy Tickets" action button.',
    value: "Scene updates and post-show audience reactions feed directly into ticket sales, constantly re-triggering discovery algorithms.",
  },
  {
    title: '"Living Weave" Strand Bookstore',
    category: "Culture & Author Events",
    onScreen: 'Live author discussion panel, "Virtual Tour" and "Shop Online" links.',
    value: "Each live signing and reading becomes a newly integrated scene, building an evergreen cultural archive that drives continuous e-commerce conversions.",
  },
];

const london = [
  {
    title: '"Living weave" London Food Stalls (Borough Market)',
    category: "Food & Street Market",
    onScreen: 'Fresh market stall display, 4.9★ rating, 22 active Weavers, "Explore Map" and "Order Ahead" buttons.',
    value: "22 local Weavers continuously refresh daily produce, rotating vendor spots, and real-time footfall, creating an interactive buying guide with mobile ordering.",
  },
  {
    title: '"Living weave" Historic Theatre & Museum (Shakespeare\'s Globe)',
    category: "Heritage & Theatre",
    onScreen: "Historic Bankside timber frame, Comedy/Tragedy theatre emblems.",
    value: "Captures new season rehearsals, performances, and backstage tours under a single root URL, accumulating search equity over time.",
  },
  {
    title: '"Living weave" Northern Chinese Cuisine (Hutong at The Shard)',
    category: "Skyline Fine Dining",
    onScreen: 'Panoramic high-altitude dining room, "£££" pricing, "last update: 15m", "Reserve table" CTA.',
    value: "Features 15-minute live updates of menu highlights and skyline weather moods, bridging discovery directly to online table booking.",
  },
  {
    title: '"Living weave" Historic Thameside Pub',
    category: "Traditional Pub & Dining",
    onScreen: 'Traditional Sunday Roast plate, "££" price tier, "Reserve table" CTA.',
    value: "Acts as a real-time atmosphere gauge along the Thames, letting creators inject fresh weekend footage that consistently drives walk-in traffic.",
  },
  {
    title: '"Living weave" The UK\'s Largest Screen (BFI IMAX)',
    category: "Cinema & Entertainment",
    onScreen: 'Featured blockbuster screening, live timetable schedules, "Buy it tickets" action link.',
    value: "Trailers and screening slots dynamically hot-swap with every new theatrical release, re-engaging the discovery algorithm to sell tickets.",
  },
  {
    title: '"Living weave" Cross Road Shop Tour (Foyles)',
    category: "Books & Culture",
    onScreen: 'Floor-to-ceiling book displays, curated categories (Literature, Comics, History), "Virtual tour access" prompt.',
    value: "Regular author signings and new releases are layered in continuously, turning the video into an evergreen, shoppable virtual bookstore tour.",
  },
];

function MapVideo({ src, poster }) {
  const playWithSound = (event) => {
    const video = event.currentTarget;
    video.muted = false;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  const keepPlayingMuted = (event) => {
    const video = event.currentTarget;
    video.muted = true;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  return (
    <div className="fp-mapPulseVideo">
      <video
        controls
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={poster}
        onMouseEnter={playWithSound}
        onMouseLeave={keepPlayingMuted}
      >
        <source src={src} type="video/mp4" />
      </video>
    </div>
  );
}

function CityCards({ items }) {
  return (
    <div className="fp-mapPulseCards">
      {items.map((item) => (
        <article className="fp-mapPulseCard" key={item.title}>
          <h3>{item.title} <span>({item.category})</span></h3>
          <p><strong>On-Screen Data:</strong> {item.onScreen}</p>
          <p><strong>Living Value:</strong> {item.value}</p>
        </article>
      ))}
    </div>
  );
}

export default function MapPage() {
  return (
    <FeaturePageShell
      pageTitle="Map"
      currentPath="/use-cases/map"
      eyebrow=""
      heroTitle="The Pulse Map: Every point reflects a venue’s latest heartbeat."
      heroText="No more fossilized pins, no invasive surveillance cameras. On Weavevo Map, every address displays a living video tile capturing the exact last verified snapshot of its Living Weave—updated whenever artisans and owners choose to pulse."
      heroImage={mapHeader}
      heroAlt="The Pulse Map: Every point reflects a venue’s latest heartbeat."
      finalEyebrow="A CITY IN NATURAL CADENCE"
      finalTitle="Turn the urban map into a breathing mosaic of the present."
      finalText="Storefronts keep control of their spatial identity while visitors see the freshest intentional visual checkpoint instead of a dead marker."
    >
      <section className="fp-section fp-mapPulseSection fp-mapPulseVideoSection">
        <div className="fp-sectionHead">
          <span className="fp-sectionEyebrow">MAP IN MOTION</span>
          <h2>Living snapshots are media, not just markers.</h2>
        </div>
        <MapVideo src={mapSection1} poster={mapHeader} />
      </section>

      <section className="fp-section fp-mapPulseSection">
        <div className="fp-sectionHead">
          <span className="fp-sectionEyebrow">BEFORE / NOW</span>
          <h2>From fossilized directory pins to merchant-driven living snapshots.</h2>
        </div>
        <div className="fp-compareGrid fp-mapPulseCompare">
          <article className="fp-comparePanel before">
            <span className="fp-cardLabel">Before: The fossilized web directory</span>
            <ul>
              {before.map(([title, text]) => <li key={title}><strong>{title}:</strong> {text}</li>)}
            </ul>
          </article>
          <article className="fp-comparePanel now">
            <span className="fp-cardLabel">Now: Merchant-driven living snapshots</span>
            <ul>
              {now.map(([title, text]) => <li key={title}><strong>{title}:</strong> {text}</li>)}
            </ul>
          </article>
        </div>
      </section>

      <section className="fp-section fp-mapPulseSection fp-mapPulseCity">
        <div className="fp-sectionHead">
          <h2>New York</h2>
        </div>
        <figure className="fp-mapPulseCityImage"><img src={mapNY} alt="" loading="lazy" /></figure>
        <CityCards items={newYork} />
        <p className="fp-mapPulseCityClosing">The glowing cyan telemetry lines mapping across Manhattan visualize Weavevo's core geographic thesis: replacing static, decaying video uploads with an interconnected city grid of perpetually updated, revenue-generating living media.</p>
      </section>

      <section className="fp-section fp-mapPulseSection fp-mapPulseCity">
        <div className="fp-sectionHead">
          <h2>Londres</h2>
        </div>
        <figure className="fp-mapPulseCityImage"><img src={mapLondon} alt="" loading="lazy" /></figure>
        <CityCards items={london} />
        <p className="fp-mapPulseCityClosing">The cyan data grid radiating across London Bridge Station highlights Weavevo’s geographic framework: replacing outdated static uploads with an interconnected urban matrix of continuously updating, revenue-yielding living video containers.</p>
      </section>

      <section className="fp-section fp-mapPulseSection fp-mapPulseVideoSection">
        <div className="fp-sectionHead">
          <h2>The Living Map: Where Video Meets the Real-Time City</h2>
        </div>
        <MapVideo src={mapSection5} poster={mapLondon} />
      </section>
    </FeaturePageShell>
  );
}
